import { Link } from "@tanstack/react-router"
import {
  dashboardDateQuery,
  type DashboardDateSelection,
  type MarketplaceId,
} from "@nova-ai/shared"
import { ArrowRight, Check, CircleSlash } from "lucide-react"
import { useState } from "react"
import {
  Area,
  Cell,
  CartesianGrid,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { formatBucketKey, formatRelativeTime } from "../../lib/format-date"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import {
  DEFAULT_MARKETPLACE_SERIES_COLOR,
  marketplaceSeriesColor,
} from "../../lib/marketplace-series"
import { convertToEur, formatCurrency, type ExchangeRates } from "../../lib/money"
import { formatPercentShare } from "../../lib/percent"
import type { MarketplaceSummary } from "../../services/use-dashboard"
import type { OfferHealthSnapshot } from "../../services/use-offer-health"
import { cn } from "../../lib/utils"
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion"
import { QueryState } from "../layout/query-state"
import { MarketplaceLogo } from "../marketplaces/marketplace-logo"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { SegmentedControl, type SegmentedOption } from "../ui/segmented-control"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"
import { DashboardDatePicker } from "./period-picker"
import { RevenueTrend } from "./revenue-trend"
import { Sparkline } from "./sparkline"

const TRAJECTORY_MODE = {
  total: "total",
  marketplaces: "marketplaces",
} as const

type TrajectoryMode = (typeof TRAJECTORY_MODE)[keyof typeof TRAJECTORY_MODE]

const PANEL_GRID =
  "grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-0 2xl:grid-cols-[minmax(0,1fr)_340px]"

const TRAJECTORY_OPTIONS: readonly SegmentedOption<TrajectoryMode>[] = [
  { value: TRAJECTORY_MODE.total, label: "Total GMV" },
  { value: TRAJECTORY_MODE.marketplaces, label: "By marketplace" },
]

type MarketplaceRevenueOverviewProps = {
  summaries: MarketplaceSummary[]
  selection: DashboardDateSelection
  onDateChange: (selection: DashboardDateSelection) => void
  rates: ExchangeRates | undefined
  offerHealthByMarketplace: Map<MarketplaceId, OfferHealthSnapshot>
  isLoading: boolean
  error: unknown
}

/** Marketplace → line/chip colour, assigned once from the summaries order
 *  so every panel colours a marketplace identically (no per-panel index math
 *  that could drift after a sort or filter). */
type ColorByMarketplace = ReadonlyMap<MarketplaceId, string>

type AnalyticsRow = {
  summary: MarketplaceSummary
  label: string
  color: string
  /** GMV converted to EUR — null when no rate is known. Per convertToEur's
   *  contract such rows fall back to their native amount in the table and stay
   *  OUT of the EUR total, chart, and pie; summing native amounts as if they
   *  were EUR would silently corrupt every aggregate. */
  gmvEur: number | null
  previousGmvEur: number | null
  sparkEur: number[]
  /** Share of the EUR total — null when the row isn't convertible. */
  share: number | null
  offerHealth: OfferHealthSnapshot | undefined
}

type ChartPoint = {
  label: string
} & Record<string, string | number>

export function MarketplaceRevenueOverview({
  summaries,
  selection,
  onDateChange,
  rates,
  offerHealthByMarketplace,
  isLoading,
  error,
}: MarketplaceRevenueOverviewProps) {
  const [selectedMarketplaces, setSelectedMarketplaces] =
    useState<ReadonlySet<MarketplaceId> | null>(null)
  const [trajectoryMode, setTrajectoryMode] = useState<TrajectoryMode>(TRAJECTORY_MODE.total)

  const colorByMarketplace: ColorByMarketplace = new Map(
    summaries.map((summary, index) => [
      summary.marketplace,
      marketplaceSeriesColor(summary.marketplace, index),
    ])
  )
  const allMarketplaces = summaries.map((summary) => summary.marketplace)
  const includedMarketplaces = selectedMarketplaces ?? new Set(allMarketplaces)
  const rows = buildAnalyticsRows({
    summaries,
    rates,
    offerHealthByMarketplace,
    includedMarketplaces,
    colorByMarketplace,
  })
  const sortedRows = [...rows].sort((a, b) => (b.gmvEur ?? 0) - (a.gmvEur ?? 0))
  const convertedRows = rows.filter((row) => row.gmvEur !== null)
  const unconvertedCount = rows.length - convertedRows.length
  const totalGmv = sum(convertedRows.map((row) => row.gmvEur ?? 0))
  const previousTotalGmv = sum(convertedRows.map((row) => row.previousGmvEur ?? 0))
  const normalizedOrderCount = sum(convertedRows.map((row) => row.summary.orderCount))
  const chartData = buildChartData(convertedRows)
  const comparisonLabel = dashboardComparisonLabel(summaries)
  // Placeholder rows keep the previous range's numbers in hand, so the KPIs
  // stay hidden until this range lands. The controls never leave: the picker
  // must stay mounted for a second range change and for focus to return to it.
  const showsMetrics = !isLoading && !error && summaries.length > 0
  function handleMarketplaceToggle(marketplace: MarketplaceId) {
    const baseline = selectedMarketplaces ?? new Set(allMarketplaces)
    const next = new Set(baseline)
    if (next.has(marketplace)) {
      next.delete(marketplace)
    } else {
      next.add(marketplace)
    }
    setSelectedMarketplaces(next)
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <PortfolioControlRow
        averageBasket={normalizedOrderCount > 0 ? totalGmv / normalizedOrderCount : 0}
        comparisonLabel={comparisonLabel}
        onDateChange={onDateChange}
        onTrajectoryModeChange={setTrajectoryMode}
        previousTotalGmv={previousTotalGmv}
        selection={selection}
        showsMetrics={showsMetrics}
        totalGmv={totalGmv}
        trajectoryMode={trajectoryMode}
        unconvertedCount={unconvertedCount}
      />

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={summaries.length === 0}
        emptyMessage="No marketplaces configured."
        embedded
      >
        <PortfolioAnalyticsPanel
          chartData={chartData}
          chartRows={convertedRows}
          shareRows={sortedRows.filter((row) => (row.share ?? 0) > 0)}
          totalGmv={totalGmv}
          trajectoryMode={trajectoryMode}
        />

        <MarketplacePulseList
          includedMarketplaces={includedMarketplaces}
          onMarketplaceToggle={handleMarketplaceToggle}
          rows={sortedRows}
          summaries={summaries}
        />
      </QueryState>
    </section>
  )
}

function dashboardComparisonLabel(summaries: MarketplaceSummary[]): string {
  return summaries[0]?.comparisonLabel ?? "vs previous range"
}

function PortfolioControlRow({
  averageBasket,
  comparisonLabel,
  onDateChange,
  onTrajectoryModeChange,
  previousTotalGmv,
  selection,
  showsMetrics,
  totalGmv,
  trajectoryMode,
  unconvertedCount,
}: {
  averageBasket: number
  comparisonLabel: string
  onDateChange: (selection: DashboardDateSelection) => void
  onTrajectoryModeChange: (mode: TrajectoryMode) => void
  previousTotalGmv: number
  selection: DashboardDateSelection
  showsMetrics: boolean
  totalGmv: number
  trajectoryMode: TrajectoryMode
  unconvertedCount: number
}) {
  return (
    <div className="px-5 pt-5 sm:px-6 sm:pt-6">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
        {showsMetrics ? (
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-[36px] font-semibold leading-none tracking-tight tabular-nums text-foreground">
                {formatCurrency(totalGmv, "EUR", 0)}
              </div>

              <RevenueTrend
                current={totalGmv}
                previous={previousTotalGmv}
                comparisonLabel={comparisonLabel}
              />
            </div>

            <div className="mt-2 text-xs text-muted-foreground">
              {unconvertedCount > 0
                ? `${unconvertedCount} excluded without a EUR rate`
                : "All selected revenue included"}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-start gap-3 sm:ml-auto sm:gap-6">
          {showsMetrics ? (
            <dl
              className="flex items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0.5"
              title="Average GMV per order"
            >
              <dt className="text-[11px] text-muted-foreground">Portfolio basket</dt>

              <dd className="text-base font-semibold tracking-tight tabular-nums text-foreground">
                {formatCurrency(averageBasket, "EUR", 0)}
              </dd>
            </dl>
          ) : null}

          <SegmentedControl
            options={TRAJECTORY_OPTIONS}
            value={trajectoryMode}
            onChange={onTrajectoryModeChange}
            label="Revenue trajectory view"
          />

          <DashboardDatePicker value={selection} onChange={onDateChange} />
        </div>
      </div>
    </div>
  )
}

function PortfolioAnalyticsPanel({
  chartData,
  chartRows,
  shareRows,
  totalGmv,
  trajectoryMode,
}: {
  chartData: ChartPoint[]
  chartRows: AnalyticsRow[]
  shareRows: AnalyticsRow[]
  totalGmv: number
  trajectoryMode: TrajectoryMode
}) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const showsMarketplaceComparison = trajectoryMode === TRAJECTORY_MODE.marketplaces

  return (
    <section className="px-5 pb-5 sm:px-6 sm:pb-6">
      <div className={PANEL_GRID}>
        <div className="flex min-w-0 flex-col pt-5 xl:pr-6">
          {showsMarketplaceComparison ? (
            <MarketplaceTrajectoryChart
              chartData={chartData}
              rows={chartRows}
              prefersReducedMotion={prefersReducedMotion}
            />
          ) : (
            <div className="min-h-96 w-full flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="portfolio-revenue-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-info)" stopOpacity={0.2} />

                      <stop offset="100%" stopColor="var(--color-info)" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />

                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />

                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value: number) => formatCurrency(value, "EUR", 0)}
                    width={64}
                  />

                  <Tooltip
                    formatter={(value) => [
                      formatCurrency(Number(value), "EUR", 0),
                      "Portfolio GMV",
                    ]}
                    contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
                    cursor={{ className: "stroke-muted-foreground" }}
                  />

                  <Area
                    type="monotone"
                    dataKey="total"
                    name="Portfolio GMV"
                    stroke="var(--color-info)"
                    strokeWidth={2.5}
                    fill="url(#portfolio-revenue-fill)"
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    isAnimationActive={!prefersReducedMotion}
                    animationDuration={560}
                    animationEasing="ease-out"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <RevenueSharePanel
          rows={shareRows}
          totalGmv={totalGmv}
          prefersReducedMotion={prefersReducedMotion}
        />
      </div>
    </section>
  )
}

function MarketplaceTrajectoryChart({
  chartData,
  rows,
  prefersReducedMotion,
}: {
  chartData: ChartPoint[]
  rows: AnalyticsRow[]
  prefersReducedMotion: boolean
}) {
  return (
    <div className="min-h-96 w-full flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} className="stroke-border/80" />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => formatCurrency(value, "EUR", 0)}
            width={64}
          />

          <Tooltip
            formatter={(value, name) => [formatCurrency(Number(value), "EUR", 0), String(name)]}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            cursor={{ className: "stroke-muted-foreground/50" }}
          />

          {rows.map((row, index) => (
            <Line
              key={row.summary.marketplace}
              type="monotone"
              dataKey={row.summary.marketplace}
              name={row.label}
              stroke={row.color}
              strokeWidth={2.25}
              strokeOpacity={0.95}
              dot={false}
              activeDot={{ r: 4, stroke: "var(--color-card)", strokeWidth: 2 }}
              isAnimationActive={!prefersReducedMotion}
              animationBegin={index * 55}
              animationDuration={520}
              animationEasing="ease-out"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function RevenueSharePanel({
  rows,
  totalGmv,
  prefersReducedMotion,
}: {
  rows: AnalyticsRow[]
  totalGmv: number
  prefersReducedMotion: boolean
}) {
  const visibleRows = rows.slice(0, 5)

  return (
    <section className="border-t border-border/70 pt-6 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-6">
      <div>
        <div className="text-[15px] font-semibold tracking-tight text-foreground">
          Revenue share
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          {rows.length === 0
            ? "No revenue share to show."
            : `GMV split across ${rows.length} ${rows.length === 1 ? "marketplace" : "marketplaces"}.`}
        </p>
      </div>

      <div className="relative mx-auto mt-3 h-[250px] w-full max-w-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="gmvEur"
              nameKey="label"
              innerRadius={72}
              outerRadius={106}
              paddingAngle={2}
              cornerRadius={5}
              stroke="var(--color-card)"
              strokeWidth={3}
              isAnimationActive={!prefersReducedMotion}
              animationDuration={480}
              animationEasing="ease-out"
            >
              {rows.map((row) => (
                <Cell key={row.summary.marketplace} fill={row.color} />
              ))}
            </Pie>

            <Tooltip
              formatter={(value, name) => [formatCurrency(Number(value), "EUR", 0), String(name)]}
              contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            />
          </PieChart>
        </ResponsiveContainer>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Total GMV
          </span>

          <span className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-foreground">
            {formatCurrency(totalGmv, "EUR", 0)}
          </span>
        </div>
      </div>

      <div className="mt-2 space-y-2.5">
        {visibleRows.map((row) => (
          <div key={row.summary.marketplace} className="flex items-center gap-3">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: row.color }} />

            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {row.label}
            </span>

            <span className="text-xs font-semibold tabular-nums text-foreground">
              {formatShare(row.share)}
            </span>
          </div>
        ))}
      </div>

      {rows.length > visibleRows.length ? (
        <div className="mt-3 border-t border-border/70 pt-3 text-xs text-muted-foreground">
          {`Remaining ${rows.length - visibleRows.length} marketplaces · ${formatShare(
            sum(rows.slice(visibleRows.length).map((row) => row.share ?? 0))
          )}`}
        </div>
      ) : null}
    </section>
  )
}

function MarketplacePulseList({
  includedMarketplaces,
  onMarketplaceToggle,
  rows,
  summaries,
}: {
  includedMarketplaces: ReadonlySet<MarketplaceId>
  onMarketplaceToggle: (marketplace: MarketplaceId) => void
  rows: AnalyticsRow[]
  summaries: MarketplaceSummary[]
}) {
  return (
    <div className="border-t border-border/70">
      <MarketplaceToggleList
        summaries={summaries}
        includedMarketplaces={includedMarketplaces}
        onToggle={onMarketplaceToggle}
        className="flex flex-wrap gap-2 bg-muted/35 px-5 py-3"
      />

      {rows.length === 0 ? (
        <div className="border-t border-border/70 px-5 py-10 text-center text-sm text-muted-foreground">
          No marketplaces selected.
        </div>
      ) : (
        <div className="divide-y divide-border/70 border-t border-border/70">
          {rows.map((row, index) => (
            <MarketplacePulseRow key={row.summary.marketplace} rank={index + 1} row={row} />
          ))}
        </div>
      )}
    </div>
  )
}

function MarketplacePulseRow({ rank, row }: { rank: number; row: AnalyticsRow }) {
  const syncedLabel = row.summary.lastSyncedAt
    ? `Synced ${formatRelativeTime(row.summary.lastSyncedAt)}`
    : "Not synced"
  const gmv =
    row.gmvEur === null
      ? formatCurrency(row.summary.gmv, row.summary.currency ?? "EUR", 0)
      : formatCurrency(row.gmvEur, "EUR", 0)

  return (
    <Link
      to="/dashboard/$marketplace"
      params={{ marketplace: row.summary.marketplace }}
      search={dashboardDateQuery(row.summary.selection)}
      className="group grid grid-cols-2 gap-4 px-5 py-4 transition-colors hover:bg-muted/40 sm:grid-cols-3 xl:grid-cols-[minmax(210px,1.25fr)_minmax(110px,0.7fr)_minmax(90px,0.55fr)_minmax(120px,0.75fr)_minmax(150px,0.9fr)_minmax(180px,1fr)_auto] xl:items-center"
    >
      <div className="col-span-2 flex min-w-0 items-center gap-3 sm:col-span-3 xl:col-span-1">
        <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {String(rank).padStart(2, "0")}
        </span>

        <MarketplaceMark marketplace={row.summary.marketplace} className="size-9" />

        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground group-hover:text-info">
            {row.label}
          </div>

          <div className="mt-0.5 text-xs text-muted-foreground">{syncedLabel}</div>
        </div>
      </div>

      <PulseMetric label="GMV" value={gmv} meta={formatShare(row.share)} />

      <PulseMetric label="Orders" value={row.summary.orderCount.toLocaleString()} />

      <div>
        <div className="text-xs text-muted-foreground">Trend</div>

        <Sparkline
          values={row.summary.spark.map((bucket) => bucket.gmv)}
          dim={row.summary.lastSyncedAt === null}
          className="mt-1 h-7"
        />
      </div>

      <OfferCoverage health={row.offerHealth} />

      <div>
        <div className="text-xs text-muted-foreground">Attention</div>

        <AttentionSignals summary={row.summary} className="mt-1" />
      </div>

      <ArrowRight className="hidden size-4 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground xl:block" />
    </Link>
  )
}

function PulseMetric({ label, value, meta }: { label: string; value: string; meta?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>

      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-sm font-semibold tabular-nums text-foreground">{value}</span>

        {meta ? <span className="text-xs tabular-nums text-muted-foreground">{meta}</span> : null}
      </div>
    </div>
  )
}

function OfferCoverage({ health }: { health: OfferHealthSnapshot | undefined }) {
  if (!health) {
    return <PulseMetric label="Offers" value="—" />
  }

  const share = health.total > 0 ? health.active / health.total : 0
  const coverage = share * 100
  const unknownLabel =
    health.unknown == null ? "activity unverified" : `${health.unknown.toLocaleString()} unknown`
  const activeLabel = `${health.active.toLocaleString()} / ${health.total.toLocaleString()} active · ${unknownLabel}`

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
        <span>Offers</span>

        <span className="tabular-nums">{formatPercentShare(share)}</span>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full",
            health.inactive > 0 || health.unknown !== 0 ? "bg-warning" : "bg-success"
          )}
          style={{ width: `${coverage}%` }}
        />
      </div>

      <div className="mt-1 text-xs tabular-nums text-muted-foreground">{activeLabel}</div>
    </div>
  )
}

function AttentionSignals({
  summary,
  className,
}: {
  summary: MarketplaceSummary
  className?: string
}) {
  const signals = [
    summary.lateOrders > 0
      ? { label: `${summary.lateOrders} late`, tone: "text-destructive" }
      : null,
    summary.awaitingShipment > 0
      ? { label: `${summary.awaitingShipment} to ship`, tone: "text-warning" }
      : null,
    summary.awaitingAcceptance > 0
      ? { label: `${summary.awaitingAcceptance} to accept`, tone: "text-muted-foreground" }
      : null,
    summary.returnsInProgress > 0
      ? { label: `${summary.returnsInProgress} returns`, tone: "text-muted-foreground" }
      : null,
  ].filter((signal): signal is { label: string; tone: string } => signal !== null)

  if (signals.length === 0) {
    return <div className={cn("text-xs text-success", className)}>All clear</div>
  }

  return (
    <div className={cn("flex flex-wrap gap-x-2 gap-y-0.5 text-xs", className)}>
      {signals.map((signal) => (
        <span key={signal.label} className={signal.tone}>
          {signal.label}
        </span>
      ))}
    </div>
  )
}

/** The include/exclude chips rendered under the chart (compact `legend`
 *  variant) and in the filter panel (`filter` variant with logo + state icon).
 *  One component so the two spots can't drift in behaviour or colour. */
function MarketplaceToggleList({
  summaries,
  includedMarketplaces,
  onToggle,
  className,
}: {
  summaries: MarketplaceSummary[]
  includedMarketplaces: ReadonlySet<MarketplaceId>
  onToggle: (marketplace: MarketplaceId) => void
  className: string
}) {
  return (
    <div className={className}>
      {summaries.map((summary) => {
        const included = includedMarketplaces.has(summary.marketplace)
        return (
          <button
            key={summary.marketplace}
            type="button"
            aria-pressed={included}
            onClick={() => onToggle(summary.marketplace)}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[13px] font-medium transition-colors",
              included
                ? "border-border bg-card text-foreground shadow-card"
                : "border-transparent bg-muted text-muted-foreground hover:text-foreground"
            )}
          >
            <MarketplaceLogo marketplace={summary.marketplace} className="size-5 rounded-md" />

            {humanizeMarketplaceId(summary.marketplace)}

            {included ? (
              <Check className="size-3.5 text-success" />
            ) : (
              <CircleSlash className="size-3.5 text-muted-foreground" />
            )}
          </button>
        )
      })}
    </div>
  )
}

function buildAnalyticsRows(input: {
  summaries: MarketplaceSummary[]
  rates: ExchangeRates | undefined
  offerHealthByMarketplace: Map<MarketplaceId, OfferHealthSnapshot>
  includedMarketplaces: ReadonlySet<MarketplaceId>
  colorByMarketplace: ColorByMarketplace
}): AnalyticsRow[] {
  const { summaries, rates, offerHealthByMarketplace, includedMarketplaces, colorByMarketplace } =
    input
  const rows = summaries
    .filter((summary) => includedMarketplaces.has(summary.marketplace))
    .map((summary) => {
      const currency = summary.currency ?? "EUR"
      return {
        summary,
        label: humanizeMarketplaceId(summary.marketplace),
        color: colorByMarketplace.get(summary.marketplace) ?? DEFAULT_MARKETPLACE_SERIES_COLOR,
        gmvEur: convertToEur(summary.gmv, currency, rates),
        previousGmvEur: convertToEur(summary.previous.gmv, currency, rates),
        sparkEur: summary.spark.map((bucket) => convertToEur(bucket.gmv, currency, rates) ?? 0),
        offerHealth: offerHealthByMarketplace.get(summary.marketplace),
      }
    })
  const totalGmv = sum(rows.map((row) => row.gmvEur ?? 0))
  return rows.map((row) => ({
    ...row,
    share: row.gmvEur === null || totalGmv === 0 ? null : row.gmvEur / totalGmv,
  }))
}

/** One chart point per server bucket. The keys come from the summaries' spark
 *  buckets (every marketplace shares the same window), so the time axis always
 *  matches the data — no client-side date math that could drift a day from the
 *  server's window. */
function buildChartData(rows: AnalyticsRow[]): ChartPoint[] {
  const keys = rows[0]?.summary.spark.map((bucket) => bucket.key) ?? []
  const granularity = rows[0]?.summary.chartGranularity ?? "day"
  return keys.map((key, index) => {
    const marketplaceValues = Object.fromEntries(
      rows.map((row) => [row.summary.marketplace, row.sparkEur[index] ?? 0])
    )
    return {
      label: formatBucketKey(key, granularity),
      total: sum(rows.map((row) => row.sparkEur[index] ?? 0)),
      ...marketplaceValues,
    }
  })
}

function formatShare(share: number | null): string {
  return share === null ? "—" : formatPercentShare(share)
}

function sum(values: number[]): number {
  return values.reduce((acc, value) => acc + value, 0)
}
